import { memo, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { StyleProp, ViewStyle, StyleSheet } from 'react-native';
import Animated, {
    useAnimatedStyle,
    useSharedValue,
    withSequence,
    withSpring,
    withRepeat,
} from 'react-native-reanimated';
import _fonts from '_fonts';
import { WW } from '_utils';
import { smallestFontSize } from '_utils/sizes';
import { useThemeContext } from '_utils/themeContext';
import { HeimeText } from 'Components';

const PULSE_ANIMATION = { SCALE_UP_DURATION: 1000, SCALE_DOWN_DURATION: 1000, MAX_SCALE: 1.08, REPEAT_COUNT: 3 };

const styles = StyleSheet.create({
    container: {
        paddingHorizontal: WW * 0.02,
        marginLeft: 'auto',
        alignItems: 'center',
        justifyContent: 'center',
        flexWrap: 'nowrap',
        display: 'flex',
        borderRadius: WW * 0.04,
        height: WW * 0.08,
    },
    text: {
        fontSize: smallestFontSize,
    },
});

type NewTagProps = {
    isNew: boolean;
    variant: 'lightGreen' | 'darkGreen';
    isVisible: boolean;
    containerStyle?: StyleProp<ViewStyle>;
};

const NewTag = ({ isNew, variant, isVisible, containerStyle }: NewTagProps) => {
    const { t } = useTranslation();
    const { theme } = useThemeContext();
    const scale = useSharedValue(1);
    const hasTriggeredPulse = useRef(false);

    const animatedStyle = useAnimatedStyle(() => ({
        transform: [{ scale: scale.value }],
    }));

    useEffect(() => {
        if (isNew && !hasTriggeredPulse.current && isVisible) {
            scale.value = withRepeat(
                withSequence(
                    withSpring(PULSE_ANIMATION.MAX_SCALE, {
                        damping: 15,
                        stiffness: 100,
                        mass: 1,
                    }),
                    withSpring(1, {
                        damping: 15,
                        stiffness: 100,
                        mass: 1,
                    }),
                ),
                PULSE_ANIMATION.REPEAT_COUNT,
                true, // Reverse animation for smoother transitions
            );
            hasTriggeredPulse.current = true;
        }
    }, [isVisible, isNew, scale]);

    if (!isNew) {
        return null;
    }

    return (
        <Animated.View
            style={[
                styles.container,
                {
                    backgroundColor: variant === 'darkGreen' ? theme.darkGreen : theme.lightGreen,
                },
                animatedStyle,
                containerStyle,
            ]}
        >
            <HeimeText
                style={[
                    styles.text,
                    {
                        color: variant === 'darkGreen' ? theme.mainText : theme.main,
                        ...(variant === 'darkGreen' && { fontFamily: _fonts.primaryFont }),
                    },
                ]}
            >
                {t('sharingAll:new')}
            </HeimeText>
        </Animated.View>
    );
};

export default memo(NewTag);
